import { type Mode } from 'core/domain/Mode'
import {
  HttpHeaders,
  type HttpClientConfig,
  type HttpClientInterface,
} from 'core/infra/http/HttpClient'

import {
  type NewShipmentCharge,
  type ShipmentAccountingSummary,
  type ShipmentCharge,
  type ShipmentChargeType,
  ModeToFullMode,
} from '../domain/ShipmentCharges'
import {
  mapApiToShipmentAccountingSummary,
  mapApiToShipmentCharge,
  mapApiToShipmentChargeType,
  mapNewShipmentChargeToApi,
  type RawShipmentChargeType,
  type RawShipmentAccountingSummary,
  type RawShipmentCharge,
} from './ShipmentChargeMapper'

export type PostShipmentChargeParams = {
  shipmentUUID: string
  newCharge: NewShipmentCharge
  config?: Partial<HttpClientConfig>
}

export type UploadChargeAttachmentParams = {
  chargeUUID: string
  file: File
  config?: Partial<HttpClientConfig>
}

type UploadedChargeAttachment = { file: string; id: number }

type UploadChargeAttachmentResponse = {
  attachments: UploadedChargeAttachment[]
}

type ShipmentChargesRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export class ShipmentChargesRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: ShipmentChargesRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getShipmentAccountingSummary(
    shipmentUUID: string,
    config?: Partial<HttpClientConfig>
  ): Promise<ShipmentAccountingSummary | undefined> {
    const endpoint = `/shipments/${shipmentUUID}/accounting-summary`

    const { data } = await this.httpClient.get<RawShipmentAccountingSummary>(endpoint, config)

    return mapApiToShipmentAccountingSummary(data)
  }

  async getShipmentCharges(
    shipmentUUID: string,
    config?: Partial<HttpClientConfig>
  ): Promise<Array<ShipmentCharge>> {
    const endpoint = `/shipments/${shipmentUUID}/charges`

    const { data = [] } = await this.httpClient.get<Array<RawShipmentCharge>>(endpoint, config)

    return data.map(charge => mapApiToShipmentCharge(charge))
  }

  async postShipmentCharge({
    shipmentUUID,
    newCharge,
    config,
  }: PostShipmentChargeParams): Promise<ShipmentCharge> {
    const endpoint = `/shipments/${shipmentUUID}/charges`
    const payload = mapNewShipmentChargeToApi(newCharge)

    const { data } = await this.httpClient.post<RawShipmentCharge>(endpoint, payload, config)

    return mapApiToShipmentCharge(data)
  }

  async uploadChargeAttachment({
    chargeUUID,
    file,
    config,
  }: UploadChargeAttachmentParams): Promise<UploadedChargeAttachment> {
    const endpoint = `/charges/${chargeUUID}/upload-file`
    const formData = new FormData()

    formData.append('attachments', file)

    const { data } = await this.httpClient.post<UploadChargeAttachmentResponse>(
      endpoint,
      formData,
      {
        headers: new HttpHeaders({
          'Content-Type': 'multipart/form-data',
        }),
        ...config,
      }
    )

    const [uploadedAttachment] = data.attachments

    return uploadedAttachment
  }

  async getShipmentChargeTypes(
    mode?: Mode,
    config?: Partial<HttpClientConfig>
  ): Promise<Array<ShipmentChargeType>> {
    let endpoint = '/charges/charge-types'

    if (mode) {
      endpoint += `?modes=${ModeToFullMode[mode]}`
    }

    const { data = [] } = await this.httpClient.get<Array<RawShipmentChargeType>>(endpoint, config)

    return data.map(charge => mapApiToShipmentChargeType(charge))
  }

  async cancelShipmentCharge(
    shipmentUUID: string,
    chargeUUID: string,
    cancelReason: string,
    config?: Partial<HttpClientConfig>
  ): Promise<ShipmentCharge> {
    const endpoint = `/shipments/${shipmentUUID}/charges/${chargeUUID}/cancel`

    const payload = { cancelation_reason: cancelReason }

    const { data } = await this.httpClient.patch<RawShipmentCharge>(endpoint, payload, config)

    return mapApiToShipmentCharge(data)
  }
}
